/**
 * FilePreviewContent — the file-rendering engine behind every preview surface.
 *
 * Extracted from FilePreviewModal so the same renderer can be embedded anywhere
 * (the Vault's file window uses it inline, FilePreviewModal wraps it in its
 * full-screen chrome). Supports PDF, images, DOCX, XLSX, Markdown and plain
 * text — exactly the set the product already handled.
 *
 * Renders CONTENT ONLY: no header, no portal, no download button. The host
 * decides the chrome.
 */

import { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { useLanguage } from '@/hooks/useLanguage';
import api from '@/services/api';
import './FilePreviewModal.css';
// A deck previews as SLIDES here, the same sheets the artifact panel draws —
// this surface used to dump the text of every slide into one wall of prose, so
// the same file looked like a different object depending on where it opened.
import { SlideDeckView, slidesFromDeckEnvelope, type Slide } from './SlideDeckView';
import ExactPagesView from './ExactPagesView';

export type PreviewKind = 'pdf' | 'image' | 'docx' | 'xlsx' | 'pptx' | 'markdown' | 'text' | 'unsupported';

type PreviewState =
  | { status: 'loading' }
  | { status: 'ready'; content: string; kind: 'text' | 'html' | 'markdown' | 'table' }
  | { status: 'ready'; blobUrl: string; kind: 'pdf' | 'image' }
  | { status: 'ready'; slides: Slide[]; kind: 'slides' }
  | { status: 'ready'; html: string; kind: 'design' }
  | { status: 'error'; message: string }
  | { status: 'unsupported' };

export function getFileExtension(fileName: string): string {
  return (fileName.split('.').pop() || '').toLowerCase();
}

/** Which renderer handles this file. Single source of truth for "can we preview it". */
export function getPreviewKind(fileName: string, fileType?: string): PreviewKind {
  const ext = getFileExtension(fileName);
  const mime = (fileType || '').toLowerCase();

  if (ext === 'pdf' || mime === 'application/pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext) || mime.startsWith('image/')) return 'image';
  // Pages, Numbers and Keynote preview as their Office twins: the server
  // serves a stored iWork file's preview AS the twin (.docx / .xlsx / .pptx),
  // so every renderer below reads it unchanged.
  if (['doc', 'docx', 'pages'].includes(ext) || mime.includes('word') || mime.includes('officedocument.wordprocessing')
      || mime.includes('apple.pages')) return 'docx';
  // .xlsm is a macro-enabled workbook — same format, and it is accepted on
  // upload, so it must preview like any other spreadsheet.
  if (['xls', 'xlsx', 'xlsm', 'numbers'].includes(ext) || mime.includes('excel') || mime.includes('spreadsheet')
      || mime.includes('apple.numbers')) return 'xlsx';
  if (['pptx', 'key'].includes(ext) || mime.includes('presentation') || mime.includes('apple.keynote')) return 'pptx';
  if (ext === 'md' || mime === 'text/markdown' || mime === 'md') return 'markdown';
  if (['txt', 'csv', 'json', 'xml', 'html'].includes(ext) || mime.startsWith('text/')) return 'text';
  return 'unsupported';
}

interface Props {
  filePath?: string;
  fileId?: string;
  localFile?: File;
  fileName: string;
  fileType?: string;
  /** Skip loading until the surface is actually shown (e.g. an inactive tab). */
  active?: boolean;
  /** Override where the bytes come from. Used to preview a PROPOSED file
   *  version (an unsaved edit) from a non-file endpoint; the extension of
   *  fileName still decides how it renders. */
  previewUrl?: string;
  /** An endpoint that answers with the laid-out PDF of THIS content. Used
   *  where the bytes are not a stored file — the version an edit card
   *  proposes — so the card shows real pages like everywhere else. */
  pagesUrl?: string;
  /** Rendered in the unsupported state, e.g. a Download button. */
  unsupportedAction?: React.ReactNode;
}

/** The server's own sentence, whatever shape the response arrived in.
 *
 *  Every branch below asks for bytes — `responseType: 'arraybuffer' | 'blob' |
 *  'text'` — because that is what a preview IS. axios honours that on error
 *  responses too, so a JSON `{error: "..."}` body comes back as an ArrayBuffer
 *  and `data.error` reads `undefined`. The reason was being fetched correctly
 *  and then thrown away: an edit card whose file had changed underneath said
 *  only "Failed to load file preview", when the server had sent a sentence
 *  naming the file and telling the user what to do about it.
 */
async function serverMessage(err: any): Promise<string | null> {
  const data = err?.response?.data;
  if (!data) return null;
  try {
    if (typeof data === 'string') return JSON.parse(data)?.error ?? null;
    if (data instanceof Blob) return JSON.parse(await data.text())?.error ?? null;
    if (data instanceof ArrayBuffer) {
      return JSON.parse(new TextDecoder().decode(data))?.error ?? null;
    }
    return data.error ?? null;
  } catch {
    // Not JSON — an HTML error page or a truncated body. The caller's generic
    // message is a better answer than a fragment of markup.
    return null;
  }
}

export function FilePreviewContent({
  filePath, fileId, localFile, fileName, fileType, active = true, unsupportedAction,
  previewUrl: previewUrlOverride, pagesUrl,
}: Props) {
  const { t } = useLanguage();
  const [state, setState] = useState<PreviewState>({ status: 'loading' });

  const kind = getPreviewKind(fileName, fileType);
  const previewUrl = previewUrlOverride
    ?? (fileId ? `/files/preview-by-id/${fileId}` : `/files/preview/${filePath}`);

  /* A stored Word document or deck is shown as its REAL pages, laid out by
     the server's document engine — the same thing the agent's panel shows.
     The conversions below (mammoth for .docx, an XML walk for .pptx) drop
     every colour, font and letterhead the file has; they stay as the
     fallback for a file with no page preview, and for bytes that live
     nowhere on the server yet (a local pick, an unsaved proposed version). */
  const [pagesUnavailable, setPagesUnavailable] = useState(false);
  const wantsPages = (kind === 'docx' || kind === 'pptx') && !pagesUnavailable
    && (!!pagesUrl || (!!fileId && !localFile && !previewUrlOverride));

  useEffect(() => { setPagesUnavailable(false); }, [fileId, pagesUrl]);

  const loadPreview = useCallback(async () => {
    if ((!filePath && !fileId && !localFile && !previewUrlOverride) || !active) return;
    // The pages component fetches for itself; do not also pull the bytes.
    if (wantsPages) return;
    setState({ status: 'loading' });

    if (kind === 'unsupported') {
      setState({ status: 'unsupported' });
      return;
    }

    const fetchAsBlob = async (): Promise<Blob> =>
      localFile || (await api.get(previewUrl, { responseType: 'blob' })).data;
    const fetchAsArrayBuffer = async (): Promise<ArrayBuffer> =>
      localFile ? localFile.arrayBuffer() : (await api.get(previewUrl, { responseType: 'arraybuffer' })).data;
    const fetchAsText = async (): Promise<string> =>
      localFile ? localFile.text() : (await api.get(previewUrl, { responseType: 'text' })).data;

    try {
      if (kind === 'pdf' || kind === 'image') {
        const blob = await fetchAsBlob();
        setState({ status: 'ready', blobUrl: URL.createObjectURL(blob), kind });
      } else if (kind === 'docx') {
        const buffer = await fetchAsArrayBuffer();
        const mammoth = await import('mammoth');
        // Word's Title and Subtitle are not in mammoth's default map, so a
        // document's own title arrived as an ordinary paragraph — the one line
        // a reader most expects to look like something, rendering as body text
        // while the headings under it rendered as headings.
        const result = await mammoth.convertToHtml({ arrayBuffer: buffer }, {
          styleMap: [
            "p[style-name='Title'] => h1.fpm-doc-title:fresh",
            "p[style-name='Subtitle'] => p.fpm-doc-subtitle:fresh",
          ],
        });
        setState({ status: 'ready', content: result.value, kind: 'html' });
      } else if (kind === 'xlsx') {
        // Render it the way the artifact viewer does — a real Excel grid with a
        // dark header band, a row-number gutter and right-aligned numbers — not
        // the bare table `sheet_to_html` emits. Same look everywhere a workbook
        // is previewed (artifact, Library file, edit card). Values only: the
        // community xlsx reader does not expose per-cell fills, so a workbook's
        // own cell colours survive in the create-time doc_model preview but not
        // here; the grid chrome carries the "this is a spreadsheet" read.
        const buffer = await fetchAsArrayBuffer();
        const XLSX = await import('xlsx');
        const workbook = XLSX.read(buffer, { type: 'array' });
        const esc = (v: unknown): string =>
          String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const isNum = (v: unknown): boolean =>
          v !== '' && v !== null && v !== undefined && typeof v !== 'boolean'
          && !Array.isArray(v) && !Number.isNaN(Number(v));
        const htmlParts: string[] = [];
        for (const sheetName of workbook.SheetNames) {
          const sheet = workbook.Sheets[sheetName];
          const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
            header: 1, blankrows: false, defval: '',
          });
          const header = aoa[0] || [];
          const body = aoa.slice(1);
          const thead = header.length
            ? `<thead><tr><th class="fpx-sheet__gutter" aria-hidden="true"></th>${
                header.map((c) => `<th>${esc(c)}</th>`).join('')
              }</tr></thead>`
            : '';
          const tbody = `<tbody>${
            body.map((row, ri) => {
              const cells = (Array.isArray(row) ? row : []).map(
                (c) => `<td${isNum(c) ? ' class="is-num"' : ''}>${esc(c)}</td>`,
              ).join('');
              return `<tr><td class="fpx-sheet__gutter">${ri + 2}</td>${cells}</tr>`;
            }).join('')
          }</tbody>`;
          htmlParts.push(
            `<section class="fpx-sheet"><div class="fpx-sheet__tab">${esc(sheetName)}</div>`
            + `<div class="fpx-sheet__scroll"><table class="fpx-sheet__table">${thead}${tbody}</table></div></section>`,
          );
        }
        setState({
          status: 'ready',
          content: `<div class="fpx-sheets">${htmlParts.join('')}</div>`,
          kind: 'table',
        });
      } else if (kind === 'pptx') {
        // A deck an agent made was RENDERED from a design skill, and that design
        // is how the artifact panel shows it. Ask the file for it first, so the
        // Library shows the same object the agent showed rather than a paler
        // structural stand-in. No design (an uploaded deck, or a tenant with no
        // skill) is the ordinary answer — the slide sheets below then say what
        // the file actually contains.
        // ASK WHENEVER WE HAVE AN ID, not only when the caller remembered to
        // say "this is a Library file". Every surface that previews a deck
        // should show the deck the way it was designed, and gating that on an
        // opt-in meant a branded deck rendered as a flat structural slide list
        // anywhere the flag was not passed — indistinguishable, to the person
        // looking at it, from the branding having been lost. A file with no
        // design (a chat attachment, an uploaded deck) answers "none" and the
        // structural render below is the right answer for it, so the cost of
        // asking is one cheap request that is already caught.
        if (fileId) {
          try {
            const { data } = await api.get(`/vault/files/${fileId}/deck-design`);
            if (typeof data?.html === 'string' && data.html) {
              setState({ status: 'ready', kind: 'design', html: data.html });
              return;
            }
          } catch {
            /* No design to be had — fall through to the slide sheets. */
          }
        }
        // Read the deck's shapes and paragraphs out of the file, then hand them
        // to the SAME renderer the artifact panel uses. `bu` mirrors what the
        // backend envelope carries: a marker only where the file has one.
        const buffer = await fetchAsArrayBuffer();
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(buffer);
        const slideNames = Object.keys(zip.files)
          .filter((n) => /^ppt\/slides\/slide\d+\.xml$/.test(n))
          .sort((a2, b2) => {
            const num = (x: string) => parseInt(x.match(/slide(\d+)\.xml/)![1], 10);
            return num(a2) - num(b2);
          });
        const unescapeXml = (x: string) => x
          .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
          .replace(/&amp;/g, '&');
        const deckSlides = [];
        for (let i = 0; i < slideNames.length; i++) {
          const xml = await zip.files[slideNames[i]].async('string');
          // One shape per <p:sp>, one paragraph per <a:p>, so the slide keeps
          // the structure the renderer expects instead of a flat list of runs.
          const shapes = [...xml.matchAll(/<p:sp>[\s\S]*?<\/p:sp>/g)].map((sm) => {
            const sp = sm[0];
            const phType = sp.match(/<p:ph[^>]*type="([^"]+)"/);
            const nameM = sp.match(/<p:cNvPr[^>]*name="([^"]*)"/);
            const paras = [...sp.matchAll(/<a:p>[\s\S]*?<\/a:p>/g)].map((pm) => {
              const ap = pm[0];
              const text = [...ap.matchAll(/<a:t>([\s\S]*?)<\/a:t>/g)]
                .map((r) => unescapeXml(r[1])).join('');
              const lvlM = ap.match(/<a:pPr[^>]*lvl="(\d+)"/);
              // sz is in hundredths of a point. Carried so the title pick can
              // use the deck's own type size, exactly as the vault path does.
              const szM = ap.match(/<a:rPr[^>]*\ssz="(\d+)"/);
              const sz = szM ? parseInt(szM[1], 10) / 100 : 0;
              const bu = /<a:buChar|<a:buAutoNum/.test(ap)
                || (!/<a:buNone/.test(ap) && /type="(body|obj)"/.test(sp));
              return { runs: [{ t: text, sz }], lvl: lvlM ? parseInt(lvlM[1], 10) : 0, bu };
            });
            return { kind: phType ? phType[1] : null, name: nameM ? nameM[1] : '', paras };
          });
          deckSlides.push({ n: i + 1, layout: '', shapes });
        }
        setState({
          status: 'ready',
          kind: 'slides',
          slides: slidesFromDeckEnvelope({ slides: deckSlides }),
        });
      } else if (kind === 'markdown') {
        setState({ status: 'ready', content: await fetchAsText(), kind: 'markdown' });
      } else {
        setState({ status: 'ready', content: await fetchAsText(), kind: 'text' });
      }
    } catch (err: any) {
      console.error('File preview error:', err);
      setState({ status: 'error', message: await serverMessage(err) || t('filePreview.loadError') });
    }
  }, [filePath, fileId, localFile, active, kind, previewUrl, wantsPages, t]);

  useEffect(() => {
    loadPreview();
    return () => {
      setState((s) => {
        if (s.status === 'ready' && 'blobUrl' in s) URL.revokeObjectURL(s.blobUrl);
        return s;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, fileId, localFile, active, wantsPages]);

  if (wantsPages) {
    return (
      <ExactPagesView
        fileId={fileId}
        url={pagesUrl}
        title={fileName}
        onUnavailable={() => setPagesUnavailable(true)}
      />
    );
  }

  if (state.status === 'loading') {
    return (
      <div className="fpm-loading">
        <div className="fpm-spinner" />
        <span>{t('filePreview.loading')}</span>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="fpm-error">
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>{state.message}</span>
      </div>
    );
  }

  if (state.status === 'unsupported') {
    return (
      <div className="fpm-unsupported">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        <p>{t('filePreview.unsupported')}</p>
        {unsupportedAction}
      </div>
    );
  }

  if ('blobUrl' in state) {
    if (state.kind === 'pdf') {
      return <iframe className="fpm-pdf-frame" src={state.blobUrl} title={fileName} />;
    }
    return (
      <div className="fpm-image-container">
        <img src={state.blobUrl} alt={fileName} className="fpm-image" />
      </div>
    );
  }

  if ('html' in state) {
    // The deck's real design, in the same frame the artifact panel uses.
    return (
      <iframe
        className="artifact-html-frame"
        title={fileName}
        sandbox="allow-same-origin"
        srcDoc={state.html}
      />
    );
  }

  if ('slides' in state) {
    // The same slide sheets the artifact panel draws, from the same component —
    // checked before the content branch because a deck carries slides, not html.
    return (
      <div className="fpm-rendered-content fpm-slides-content">
        <SlideDeckView slides={state.slides} />
      </div>
    );
  }

  if ('content' in state) {
    if (state.kind === 'html' || state.kind === 'table') {
      // Sanitize before splicing into the DOM — preview content can originate
      // from arbitrary user-uploaded HTML or DOCX-to-HTML conversion output.
      // Without sanitization this is a cross-tenant stored XSS sink.
      const safe = DOMPurify.sanitize(state.content, {
        USE_PROFILES: { html: true },
        FORBID_TAGS: ['style', 'script'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick'],
      });
      return (
        <div
          className={`fpm-rendered-content ${state.kind === 'table' ? 'fpm-table-content' : 'fpm-docx-content'}`}
          dangerouslySetInnerHTML={{ __html: safe }}
        />
      );
    }
    if (state.kind === 'markdown') {
      return (
        <div className="fpm-rendered-content fpm-markdown-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{state.content}</ReactMarkdown>
        </div>
      );
    }
    return <pre className="fpm-text-content">{state.content}</pre>;
  }

  return null;
}

export default FilePreviewContent;
